import React from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { Section, SectionHeading, WarningBox, InfoBox } from "@/components/Primitives";
import { CTABand } from "@/components/PageHero";
import WorldMap from "@/components/WorldMap";
import Testimonials from "@/components/Testimonials";
import AssessmentForm from "@/components/AssessmentForm";
import { useSite } from "@/context/SiteContext";
import {
  HERO_TRUST, LABUAN_BENEFITS, LABUAN_SUITS, PROCESS_STEPS, WHY_CHOOSE,
  MIGRATION_STRATEGIES, MIGRATION_WARNING, COUNTRIES,
} from "@/data/content";

const Ico = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };
const HERO_IMG = "https://images.unsplash.com/photo-1470217957101-da7150b9b681?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const navigate = useNavigate();
  const { openWhatsApp } = useSite();

  return (
    <>
      <SEO title="Labuan & Malaysia Business Setup, Global Expansion"
        description="Global Pass Consultants helps foreign entrepreneurs set up companies in Labuan and Malaysia and expand across Canada, the UK and South Asia. Contact us to know more."
        path="/"
        jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "Global Pass Consultants", areaServed: ["Malaysia", "Canada", "United Kingdom", "Pakistan", "India"] }} />

      {/* HERO */}
      <section className="relative bg-navy text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Kuala Lumpur skyline" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/70" />
        </div>
        <div className="absolute inset-0 geo-overlay opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-gold border border-gold/30 rounded-full px-4 py-1.5">
              Malaysia Headquarters | International Service Network
            </span>
            <h1 data-testid="hero-title" className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Build Your Business in Malaysia. <span className="text-gold">Expand It Across the World.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
              From Labuan company formation and ongoing compliance to international business expansion and investment-based mobility, Global Pass Consultants connects entrepreneurs with the right professional support across Malaysia, Canada, the United Kingdom and South Asia.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/labuan-company-setup" data-testid="hero-cta-labuan" className="btn-gold rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2">
                Explore Labuan Setup <ArrowRight size={18} />
              </Link>
              <button onClick={() => navigate("/contact")} className="rounded-full px-7 py-3.5 font-bold bg-white/10 border-2 border-white/30 hover:bg-white/20 transition-colors">
                Book a Consultation
              </button>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
              {HERO_TRUST.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-200">
                  <span className="w-5 h-5 rounded-full bg-emerald-brand flex items-center justify-center shrink-0"><Check size={12} /></span>{t}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 fade-up hidden lg:block" style={{ animationDelay: "0.15s" }}>
            <WorldMap />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <Section className="bg-white" pattern>
        <SectionHeading eyebrow="Malaysia at the Centre" title="Malaysia at the Centre of Your Global Business Strategy"
          subtitle="Global Pass Consultants helps entrepreneurs assess, establish and manage business structures in Malaysia, with a special focus on Labuan IBFC. We also support business owners looking to enter international markets through company formation, business acquisition, investment and local professional partnerships." center />
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div data-testid="service-card-enter-malaysia" className="group rounded-2xl bg-navy text-white p-8 hover-lift overflow-hidden relative">
            <div className="absolute inset-0 songket-pattern opacity-20" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center mb-5"><Ico name="LogIn" className="text-gold" /></div>
              <h3 className="text-2xl font-bold">Enter Malaysia</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">Labuan incorporation, Malaysian company setup, compliance coordination, business activation and market-entry support. Contact us to know more.</p>
              <Link to="/labuan-company-setup" className="mt-6 inline-flex items-center gap-2 text-gold font-semibold group-hover:gap-3 transition-all">Explore Labuan <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div data-testid="service-card-expand-globally" className="group rounded-2xl bg-emerald-brand text-white p-8 hover-lift overflow-hidden relative">
            <div className="absolute inset-0 songket-pattern opacity-20" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-5"><Ico name="Globe2" className="text-white" /></div>
              <h3 className="text-2xl font-bold">Expand Globally</h3>
              <p className="mt-3 text-emerald-50 leading-relaxed">International company formation, business purchase support, investment exploration and regulated immigration referral. Contact us to know more.</p>
              <Link to="/global-business-expansion" className="mt-6 inline-flex items-center gap-2 text-gold font-semibold group-hover:gap-3 transition-all">Explore Expansion <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </Section>

      {/* MAIN LABUAN SECTION */}
      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Our Primary Advisory" title="Why International Entrepreneurs Consider Labuan"
          subtitle="Labuan IBFC is a recognised Malaysian international business and financial centre. Here is why it appeals to internationally minded founders." />
        <div data-testid="labuan-benefits-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {LABUAN_BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl bg-white border border-slate-100 p-7 hover-lift">
              <div className="w-12 h-12 rounded-xl bg-navy/5 flex items-center justify-center mb-4"><Ico name={b.icon} className="text-navy" /></div>
              <h3 className="text-lg font-bold text-navy">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8"><InfoBox>Labuan incorporation must be completed through a licensed Labuan trust company. Global Pass Consultants coordinates the process with appropriately licensed and regulated service providers. Contact us to know more.</InfoBox></div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/labuan-company-setup" className="btn-gold rounded-full px-6 py-3 font-bold">View Complete Labuan Guide</Link>
          <Link to="/eligibility" className="rounded-full px-6 py-3 font-semibold border-2 border-navy text-navy hover:bg-navy hover:text-white transition-colors">Request a Preliminary Assessment</Link>
        </div>
      </Section>

      {/* WHO LABUAN SUITS */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Suitability" title="Could Labuan Fit Your Business?" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {LABUAN_SUITS.map((s) => (
            <div key={s} className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-brand/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={14} className="text-emerald-brand" /></span>
              <span className="text-sm font-medium text-navy">{s}</span>
            </div>
          ))}
        </div>
        <div className="mt-8"><WarningBox testid="labuan-suits-warning">Not every activity receives the same tax, substance, licensing or work-permit treatment. Your proposed business model must be reviewed before selecting Labuan. Contact us to know more.</WarningBox></div>
      </Section>

      {/* PROCESS */}
      <Section dark>
        <SectionHeading eyebrow="Our Process" title="From Business Idea to Operating Structure" light
          subtitle="A structured path with no guaranteed approval or fixed timeline. Where you need more information, contact us to know more." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROCESS_STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl bg-navy-surface/60 border border-white/10 p-6">
              <div className="text-gold font-extrabold text-3xl">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-2 font-bold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-300">{s.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* GLOBAL EXPANSION */}
      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Beyond Malaysia" title="From Malaysia to International Markets"
          subtitle="We help established business owners assess international markets, identify suitable structures and coordinate local support for company setup, business purchase, investment and commercial expansion. Contact us to know more." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {COUNTRIES.map((c) => (
            <Link key={c.slug} to={`/countries/${c.slug}`} className="group rounded-2xl bg-white border border-slate-100 overflow-hidden hover-lift">
              <div className="h-36 overflow-hidden"><img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-5">
                <div className="flex items-center gap-2"><span className="text-xl">{c.flag}</span><h3 className="font-bold text-navy">{c.name}</h3></div>
                <p className="mt-1 text-sm text-slate-500">{c.tagline}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-emerald-brand group-hover:gap-2 transition-all">Contact us to know more <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
          <div className="rounded-2xl bg-navy text-white p-6 flex flex-col justify-center">
            <h3 className="font-bold text-lg">Other markets</h3>
            <p className="mt-2 text-sm text-slate-300">Additional markets are supported through professional partners. Contact us to know more.</p>
          </div>
        </div>
      </Section>

      {/* MIGRATION */}
      <Section className="bg-white">
        <SectionHeading eyebrow="Business & Investment Migration" title="Business Expansion That May Support Your Mobility Goals"
          subtitle="For some entrepreneurs, international expansion is connected with the goal of living and working in another country. We help clients assess the business side of that plan and coordinate immigration matters with appropriately licensed professionals. Contact us to know more." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MIGRATION_STRATEGIES.map((m) => (
            <div key={m.title} className="rounded-2xl border border-slate-100 p-6 hover-lift bg-slate-50/60">
              <div className="w-11 h-11 rounded-xl bg-gold/15 flex items-center justify-center mb-4"><Ico name={m.icon} className="text-gold" /></div>
              <h3 className="font-bold text-navy text-[15px]">{m.title}</h3>
            </div>
          ))}
        </div>
        <div className="mt-8"><WarningBox testid="regulatory-warning-box">{MIGRATION_WARNING}</WarningBox></div>
        <div className="mt-6"><Link to="/business-investment-migration" className="btn-gold rounded-full px-6 py-3 font-bold inline-flex items-center gap-2">Request a Business Mobility Assessment <ArrowRight size={16} /></Link></div>
      </Section>

      {/* WHY CHOOSE */}
      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Why Choose Us" title="A Serious International Business Advisory Firm" center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {WHY_CHOOSE.map((w) => (
            <div key={w} className="rounded-xl bg-white border border-slate-100 p-6 flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-emerald-brand text-white flex items-center justify-center shrink-0"><Check size={15} /></span>
              <span className="text-sm font-medium text-navy">{w}</span>
            </div>
          ))}
        </div>
        <p className="text-center mt-8"><Link to="/contact" className="text-emerald-brand font-semibold hover:underline">Contact us to know more.</Link></p>
      </Section>

      {/* GLOBAL PRESENCE */}
      <Section dark>
        <SectionHeading eyebrow="Global Presence" title="Malaysian Roots. International Reach." light
          subtitle="Hover over each location to explore how we coordinate support across markets." />
        <WorldMap />
      </Section>

      <Testimonials />

      {/* ASSESSMENT WIDGET */}
      <Section className="bg-white" pattern>
        <div className="max-w-3xl mx-auto rounded-3xl border border-slate-100 shadow-xl p-6 md:p-10 bg-white">
          <SectionHeading eyebrow="Confidential Assessment" title="Start Your Business Assessment" center />
          <AssessmentForm compact />
        </div>
      </Section>

      <CTABand />
    </>
  );
}
